import { BookedParty } from '@types';
import { PDFViewerModal } from '_components/atoms';
import UserService from '_services/UserService';
import HelperMethods from '_utils/HelperMethods';
import React, { useEffect, useState } from 'react';
import Barcode from 'react-barcode';

const MyBookedParties: React.FC = () => {
  const [parties, setParties] = useState<BookedParty[]>([]);
  const [loading, setLoading] = useState(true);
  const [pdfViewerModal, setPdfViewerModal] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
  }>({
    isOpen: false,
    url: '',
    title: '',
  });

  const fetchParties = async () => {
    try {
      setLoading(true);

      const responseData =
        (await UserService.getBookedParties()) as unknown as BookedParty[];

      if (responseData) {
        setParties(responseData);
      }
    } catch (error) {
      console.error('Error fetching parties:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParties();
  }, []);

  const openPdfViewer = (url: string, title: string) => {
    setPdfViewerModal({
      isOpen: true,
      url,
      title,
    });
  };

  const closePdfViewer = () => {
    setPdfViewerModal({
      isOpen: false,
      url: '',
      title: '',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            My Booked Parties
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            View your booked party reservations
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <div className="text-sm text-gray-600">
            Total Parties:{' '}
            <span className="font-semibold">{parties.length}</span>
          </div>
        </div>
      </div>

      {/* Parties Table - Desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Party Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {parties.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    {loading ? 'Loading...' : 'No parties found'}
                  </td>
                </tr>
              ) : (
                parties.map(party => (
                  <tr key={party.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col items-center">
                        <div className="mb-2">
                          <Barcode
                            value={party.barcodeId}
                            width={1}
                            height={50}
                            fontSize={10}
                            format="CODE128"
                          />
                        </div>
                        <div className="text-xs text-gray-500 text-center">
                          {party.barcodeId}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {party.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {HelperMethods.formatDate(party.sessionDate)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {HelperMethods.formatTime12Hour(party.timeSlot)}
                      </div>
                      <div className="text-xs text-gray-400">
                        Receipt: {party.receiptNumber}
                      </div>
                      {party.pizzaList && (
                        <div className="text-xs text-gray-400 mt-1">
                          Pizza: {party.pizzaList}
                        </div>
                      )}
                      {party.guestName && (
                        <div className="text-xs text-gray-400 mt-1">
                          Guest: {party.guestName}
                        </div>
                      )}
                      {party.notes && (
                        <div className="text-xs text-gray-400 mt-1">
                          Notes: {party.notes}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {party.customerName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {party.customerEmail}
                      </div>
                      <div className="text-sm text-gray-500">
                        {party.customerPhone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          party.redeemed
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {party.redeemed ? 'Redeemed' : 'Not Redeemed'}
                      </span>
                      {party.redeemed && party.redeemedBy && (
                        <div className="text-xs text-gray-500 mt-1">
                          by {party.redeemedBy}
                        </div>
                      )}
                      {party.redeemed && party.redeemedAt && (
                        <div className="text-xs text-gray-500 mt-1">
                          Redeemed on{' '}
                          {HelperMethods.formatDateTimeLocal(party.redeemedAt)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2 justify-between">
                        {party.receiptUrl && (
                          <button
                            onClick={() =>
                              openPdfViewer(party.receiptUrl!, 'View Receipt')
                            }
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View Receipt
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Parties Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {parties.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            {loading ? 'Loading...' : 'No parties found'}
          </div>
        ) : (
          parties.map(party => (
            <div
              key={party.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
            >
              {/* Party Info */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {party.name}
                  </h3>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      party.redeemed
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {party.redeemed ? 'Redeemed' : 'Not Redeemed'}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  {HelperMethods.formatDate(party.sessionDate)}
                </div>
                <div className="text-sm text-gray-600">
                  {HelperMethods.formatTime12Hour(party.timeSlot)}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Receipt: {party.receiptNumber}
                </div>
                {party.pizzaList && (
                  <div className="text-xs text-gray-400 mt-1">
                    Pizza: {party.pizzaList}
                  </div>
                )}
                {party.guestName && (
                  <div className="text-xs text-gray-400 mt-1">
                    Guest: {party.guestName}
                  </div>
                )}
                {party.notes && (
                  <div className="text-xs text-gray-400 mt-1">
                    Notes: {party.notes}
                  </div>
                )}
              </div>

              {/* Customer Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Customer
                </h4>
                <div className="text-sm text-gray-600">
                  <div className="font-medium">{party.customerName}</div>
                  <div>{party.customerEmail}</div>
                  <div>{party.customerPhone}</div>
                </div>
              </div>

              {/* Barcode Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <div className="flex flex-col items-center">
                  <div className="mb-2">
                    <Barcode
                      value={party.barcodeId}
                      width={1}
                      height={60}
                      fontSize={12}
                      format="CODE128"
                    />
                  </div>
                  <div className="text-xs text-gray-500 text-center">
                    {party.barcodeId}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {party.receiptUrl && (
                  <button
                    onClick={() =>
                      openPdfViewer(party.receiptUrl!, 'View Receipt')
                    }
                    className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                  >
                    View Receipt
                  </button>
                )}
              </div>

              {/* Redeemed By Info */}
              {party.redeemed && party.redeemedBy && (
                <div className="mt-3 text-xs text-gray-500">
                  Redeemed by: {party.redeemedBy}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfViewerModal.isOpen}
        onClose={closePdfViewer}
        url={pdfViewerModal.url}
        title={pdfViewerModal.title}
      />
    </div>
  );
};

export default MyBookedParties;
